import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PlanLimitError } from '../../../core/models/plan.model';

export interface UpgradeDialogData {
  limitType: string;
  currentCount: number;
  maxAllowed: number;
  planName?: string;
}

@Component({
  selector: 'app-upgrade-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule
  ],
  template: `
    <div class="upgrade-dialog">
      <div class="dialog-icon">
        <mat-icon>lock</mat-icon>
      </div>

      <h2 mat-dialog-title>{{ 'plan.upgrade.title' | translate }}</h2>

      <mat-dialog-content>
        <p class="limit-message">
          {{ getLimitMessage() }}
        </p>

        <div class="usage-info" *ngIf="data.limitType !== 'export' && data.limitType !== 'archivedClient'">
          <div class="usage-bar">
            <div class="usage-fill" [style.width.%]="getUsagePercent()"></div>
          </div>
          <span class="usage-text">
            {{ data.currentCount }} / {{ data.maxAllowed }}
          </span>
        </div>

        <p class="upgrade-prompt">
          {{ 'plan.upgrade.prompt' | translate }}
        </p>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button mat-dialog-close>
          {{ 'common.close' | translate }}
        </button>
        <button mat-flat-button color="primary" (click)="viewPlans()">
          <mat-icon>rocket_launch</mat-icon>
          {{ 'plan.upgrade.viewPlans' | translate }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .upgrade-dialog {
      text-align: center;
      padding: 1rem;
    }

    .dialog-icon {
      display: flex;
      justify-content: center;
      margin-bottom: 1rem;

      mat-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
        color: var(--warn-color, #f44336);
      }
    }

    h2 {
      font-size: 1.5rem;
      margin-bottom: 1rem;
    }

    .limit-message {
      font-size: 1rem;
      color: var(--text-secondary);
      margin-bottom: 1.5rem;
    }

    .usage-info {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
    }

    .usage-bar {
      width: 100%;
      max-width: 300px;
      height: 8px;
      background-color: var(--surface-variant);
      border-radius: 4px;
      overflow: hidden;
    }

    .usage-fill {
      height: 100%;
      background-color: var(--warn-color, #f44336);
      border-radius: 4px;
      transition: width 0.3s ease;
    }

    .usage-text {
      font-size: 0.9rem;
      color: var(--text-secondary);
    }

    .upgrade-prompt {
      font-size: 1rem;
      font-weight: 500;
      color: var(--text-primary);
    }

    mat-dialog-actions {
      margin-top: 1rem;

      button {
        mat-icon {
          margin-right: 0.5rem;
        }
      }
    }

    :host-context([dir="rtl"]) {
      mat-dialog-actions button mat-icon {
        margin-right: 0;
        margin-left: 0.5rem;
      }
    }
  `]
})
export class UpgradeDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UpgradeDialogData,
    private dialogRef: MatDialogRef<UpgradeDialogComponent>,
    private router: Router,
    private translateService: TranslateService
  ) {}

  getLimitMessage(): string {
    const limitType = this.data.limitType;
    if (limitType === 'clients') {
      return this.translateService.instant('plan.upgrade.clientLimit', { max: this.data.maxAllowed });
    } else if (limitType === 'invoices') {
      return this.translateService.instant('plan.upgrade.invoiceLimit', { max: this.data.maxAllowed });
    } else if (limitType === 'export') {
      return this.translateService.instant('plan.upgrade.exportLimit');
    } else if (limitType === 'contracts') {
      return this.translateService.instant('plan.upgrade.contractLimit');
    } else if (limitType === 'archivedClient') {
      return this.translateService.instant('plan.upgrade.archivedClientLimit');
    }
    return this.translateService.instant('plan.upgrade.prompt');
  }

  getUsagePercent(): number {
    if (this.data.maxAllowed === 0) return 100;
    return Math.min((this.data.currentCount / this.data.maxAllowed) * 100, 100);
  }

  viewPlans(): void {
    this.dialogRef.close();
    this.router.navigate(['/pricing']);
  }
}
